import styled from '@emotion/styled'
import {
    ApiReviewMediaItem,
    INSPECT_OVERLAY_ATTRS,
} from '@storefronts/common-types'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getReviewMedia } from '../../../../api'
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import {
    inScrollingDesktop,
    pageBleedTrack,
    scaledFontSize,
} from '../../../style/scrollingLayout'
import {
    hoverFadeText,
    hoverInvertOutline,
} from '../../../style/interactiveStates'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'

interface CustomerPhotosProps {
    linkHash: string
    initialMedia?: ApiReviewMediaItem[]
    shopifyProductId?: string | null
    /** Section-owned strips bleed; nested strips in reviews do not. */
    bleed?: boolean
}

function shouldUsePreloadedFirstPage(
    initialMedia: ApiReviewMediaItem[] | undefined,
    shopifyProductId: string | null | undefined,
): boolean {
    return Boolean(initialMedia?.length) && !shopifyProductId
}

function Lightbox({
    item,
    photos,
    index,
    onClose,
    onPrev,
    onNext,
    onLoadMore,
    hasMore,
}: {
    item: ApiReviewMediaItem
    photos: ApiReviewMediaItem[]
    index: number
    onClose: () => void
    onPrev: () => void
    onNext: () => void
    onLoadMore: () => void
    hasMore: boolean
}) {
    const isLast = index === photos.length - 1
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        closeRef.current?.focus()
    }, [])

    useBodyScrollLock(true)

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') onPrev()
            else if (e.key === 'ArrowRight') {
                if (isLast && hasMore) onLoadMore()
                else onNext()
            } else if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [onPrev, onNext, onClose, onLoadMore, isLast, hasMore])

    return createPortal(
        <div
            {...INSPECT_OVERLAY_ATTRS}
            role="dialog"
            aria-modal="true"
            aria-label="Customer photo lightbox"
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: 'rgba(0,0,0,0.92)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                    position: 'relative',
                }}
            >
                <img
                    src={item.url}
                    alt={`Customer photo ${index + 1}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 8,
                    }}
                />
                {index > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            onPrev()
                        }}
                        style={navButtonStyle('left')}
                        aria-label="Previous"
                    >
                        ‹
                    </button>
                )}
                {(index < photos.length - 1 || hasMore) && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            if (isLast && hasMore) onLoadMore()
                            else onNext()
                        }}
                        style={navButtonStyle('right')}
                        aria-label="Next"
                    >
                        ›
                    </button>
                )}
            </div>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    flexShrink: 0,
                    maxHeight: '40vh',
                    overflowY: 'auto',
                    padding: '0 24px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    boxSizing: 'border-box',
                }}
            >
                {item.text ? (
                    <p
                        style={{
                            margin: 0,
                            maxWidth: 'min(90vw, 640px)',
                            color: 'white',
                            ...getTypographyRoleStyle('bodyText', {
                                lineHeight: 1.5,
                            }),
                            fontSize: scaledFontSize(14),
                            textAlign: 'center',
                            overflowWrap: 'break-word',
                        }}
                    >
                        {item.text}
                    </p>
                ) : null}
                <div
                    style={{
                        color: 'rgba(255,255,255,0.6)',
                        ...getTypographyRoleStyle('captionText'),
                        fontSize: scaledFontSize(13),
                    }}
                >
                    {index + 1} /{' '}
                    {hasMore ? `${photos.length}+` : photos.length}
                </div>
            </div>
            <button
                ref={closeRef}
                onClick={(e) => {
                    e.stopPropagation()
                    onClose()
                }}
                style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: scaledFontSize(28),
                    cursor: 'pointer',
                    lineHeight: 1,
                }}
                aria-label="Close"
            >
                ✕
            </button>
        </div>,
        document.body,
    )
}

function navButtonStyle(side: 'left' | 'right'): React.CSSProperties {
    return {
        position: 'absolute',
        [side]: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'rgba(255,255,255,0.15)',
        border: 'none',
        color: 'white',
        fontSize: scaledFontSize(40),
        lineHeight: 1,
        width: 48,
        height: 48,
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }
}

const Section = styled.div`
    margin-bottom: 24px;
`

const SectionLabel = styled.h2({
    ...getTypographyRoleStyle('headingText', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    margin: `0 0 ${Constants.SECTION_BASE_SPACING_PX}px`,
})

// Roomier photos where the strip is wide enough to take them. A phone is not:
// at 160 the thumbnails eat most of the screen.
const THUMBNAIL_PX = 80
const THUMBNAIL_PX_WIDE = 160

const PhotoStrip = styled.div<{ bleed: boolean }>(({ bleed }) => ({
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    paddingBottom: 4,
    ...(bleed ? pageBleedTrack : null),
}))

const ThumbnailButton = styled.button({
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    ...hoverFadeText(),
})

const ThumbnailImage = styled.img({
    width: THUMBNAIL_PX,
    height: THUMBNAIL_PX,
    objectFit: 'cover',
    borderRadius: 6,
    display: 'block',
    ...inScrollingDesktop({
        width: THUMBNAIL_PX_WIDE,
        height: THUMBNAIL_PX_WIDE,
    }),
})

const LoadMoreButton = styled.button({
    width: THUMBNAIL_PX,
    height: THUMBNAIL_PX,
    flexShrink: 0,
    borderRadius: 6,
    border: `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    background: 'transparent',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('buttonText', {
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(12),
    cursor: 'pointer',
    ...hoverInvertOutline(
        `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    ),
    ...inScrollingDesktop({
        width: THUMBNAIL_PX_WIDE,
        height: THUMBNAIL_PX_WIDE,
    }),
})

/**
 * State is only initialized from props on mount; callers must pass a `key`
 * derived from `linkHash`/`shopifyProductId` to reset it on product or
 * variant change (see usage in Reviews.tsx and ReviewsImagesSection.tsx).
 */
export const CustomerPhotos: React.FC<CustomerPhotosProps> = ({
    linkHash,
    initialMedia,
    shopifyProductId,
    bleed = true,
}) => {
    const [photos, setPhotos] = useState<ApiReviewMediaItem[]>(() =>
        shouldUsePreloadedFirstPage(initialMedia, shopifyProductId)
            ? (initialMedia ?? [])
            : [],
    )
    const [page, setPage] = useState(() =>
        shouldUsePreloadedFirstPage(initialMedia, shopifyProductId) ? -1 : 0,
    )
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(
        () => !shouldUsePreloadedFirstPage(initialMedia, shopifyProductId),
    )
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

    useEffect(() => {
        if (page < 0) return
        let cancelled = false
        setLoading(true)
        // hasMore needs no reset here: it is only read while photos are shown,
        // and it is rewritten together with the photos when the fetch resolves.
        if (page === 0) {
            setPhotos([])
        }
        getReviewMedia(linkHash, page, shopifyProductId)
            .then((r) => {
                if (cancelled) return
                if (r.isError) return
                setPhotos((prev) =>
                    page === 0 ? r.result.media : [...prev, ...r.result.media],
                )
                setHasMore(r.result.hasMore)
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [linkHash, page, shopifyProductId])

    if (!loading && photos.length === 0) return null

    const handleLoadMore = () => {
        setPage((p) => (p < 0 ? 1 : p + 1))
    }

    const activeLightboxItem =
        lightboxIndex !== null &&
        lightboxIndex >= 0 &&
        lightboxIndex < photos.length
            ? photos[lightboxIndex]
            : undefined

    return (
        <>
            {activeLightboxItem && lightboxIndex !== null && (
                <Lightbox
                    item={activeLightboxItem}
                    photos={photos}
                    index={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onPrev={() =>
                        setLightboxIndex((i) => Math.max(0, (i ?? 0) - 1))
                    }
                    onNext={() =>
                        setLightboxIndex((i) =>
                            Math.min(photos.length - 1, (i ?? 0) + 1),
                        )
                    }
                    onLoadMore={handleLoadMore}
                    hasMore={hasMore}
                />
            )}
            <Section>
                <SectionLabel>Customer photos &amp; videos</SectionLabel>
                <PhotoStrip bleed={bleed}>
                    {photos.map((item, i) => (
                        <ThumbnailButton
                            key={`${item.url}-${i}`}
                            onClick={() => setLightboxIndex(i)}
                            aria-label={`Open customer photo ${i + 1}`}
                        >
                            <ThumbnailImage
                                src={item.url}
                                alt={`Customer photo ${i + 1}`}
                            />
                        </ThumbnailButton>
                    ))}
                    {hasMore && (
                        <LoadMoreButton onClick={handleLoadMore}>
                            Load more
                        </LoadMoreButton>
                    )}
                </PhotoStrip>
            </Section>
        </>
    )
}
